import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ScrollPosition, trackWindowScroll } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import FullScreenViewer, {
	FullScreenViewerProps,
} from "../../../../components/common/FullScreenViewer/FullScreenViewer";
import BriaImage, { BriaImageProps, ImageErrorType } from "../../../../components/common/Galleries/BriaImage";
import { useAppStore } from "../../../../hooks/useStores";
import { PlaygroundImage, PlaygroundResult } from "../../../../models/image-to-image";
import useImageUtils from "../../../../utils/useImageUtils";
import styles from "./LabGalleryView.module.scss";
import ThumbnailSelector from "./ThumbnailSelector/ThumbnailSelector";

type LabGalleryImageItemProps = {
	image: PlaygroundImage;
	isSelected: boolean;
	scrollPosition: ScrollPosition;
	renderImageOverlay?: (image: PlaygroundImage) => ReactNode;
	renderImageExtra?: (
		image: PlaygroundImage,
		imageElement: HTMLImageElement | null,
		isSelected: boolean,
	) => ReactNode;
	extraProps: Partial<BriaImageProps>;
	onOpenFullScreen: () => void;
	onSuccessPulling?: (result?: any) => void;
	onErrorPulling?: (errorType: ImageErrorType) => void;
	abortController?: AbortController;
};

const LabGalleryImageItem = observer(
	({
		image,
		isSelected,
		scrollPosition,
		renderImageOverlay,
		renderImageExtra,
		extraProps,
		onOpenFullScreen,
		onSuccessPulling,
		onErrorPulling,
		abortController,
	}: LabGalleryImageItemProps) => {
		const { playgroundStore } = useAppStore();
		const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);

		return (
			<Box
				className={clsx(styles.imageWrapper, {
					[styles.hidden]: !isSelected,
				})}
			>
				<BriaImage
					url={image.url}
					image={image as BriaImageProps & PlaygroundImage}
					loading={image.loading}
					errorType={image.error?.type}
					displayOverlay="customHover"
					ImageOverlay={renderImageOverlay?.(image)}
					handleFullScreen={onOpenFullScreen}
					handleClick={onOpenFullScreen}
					lazyImageProps={{
						scrollPosition,
						onLoad: (e?: React.SyntheticEvent<HTMLImageElement, Event>) => {
							playgroundStore.addLoadedImageToStore(image);
							if (e?.currentTarget) {
								setImageElement(e.currentTarget);
							}
						},
					}}
					hideDownloadIcon={false}
					gridView={true}
					selectable={true}
					{...(image.status_url && {
						async: true,
						asyncProps: {
							isV2: true,
							statusUrl: image.status_url,
							maxAttempts: 360,
							sleepSecs: 2,
							abortController,
						},
						onSuccessPulling,
						onErrorPulling,
					})}
					{...extraProps}
				/>
				{renderImageExtra?.(image, imageElement, isSelected)}
			</Box>
		);
	},
);

export type LabGalleryViewProps = {
	playgroundResult: PlaygroundResult;
	className?: string;
	fullScreenProps?: Partial<FullScreenViewerProps>;
	scrollPosition: ScrollPosition;
	renderImageOverlay?: (image: PlaygroundImage) => ReactNode;
	renderImageExtra?: (
		image: PlaygroundImage,
		imageElement: HTMLImageElement | null,
		isSelected: boolean,
	) => ReactNode;
	getImageProps?: (image: PlaygroundImage) => Partial<BriaImageProps>;
	onSuccessPulling?: (image: PlaygroundImage, result?: any) => void;
	onErrorPulling?: (image: PlaygroundImage, errorType: ImageErrorType) => void;
	onSelectedImageChange?: (image: PlaygroundImage, index: number) => void;
	abortController?: AbortController;
	footerControls?: ReactNode;
	onImageSelect?: (image: PlaygroundImage, index: number) => void;
	isSelected?: boolean;
	anonymousFeature?: string;
};

const LabGalleryView = ({
	playgroundResult,
	className,
	fullScreenProps,
	scrollPosition,
	renderImageOverlay,
	renderImageExtra,
	getImageProps,
	onSuccessPulling,
	onErrorPulling,
	onSelectedImageChange,
	abortController,
	footerControls,
	onImageSelect,
	isSelected,
	anonymousFeature,
}: LabGalleryViewProps) => {
	const { playgroundStore, authStore, anonymousUsageStore } = useAppStore();
	const { exportImage } = useImageUtils();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [isFullscreenViewerOpen, setIsFullscreenViewerOpen] = useState<boolean>(!!fullScreenProps?.open);
	const [displayMode, setDisplayMode] = useState(fullScreenProps?.displayMode);
	const hasFirstImageLoadedRef = useRef(false);
	const consumedImageIdsRef = useRef(new Set<string>());

	const images = playgroundResult.images;

	const effectiveIsSelected = isSelected !== undefined ? isSelected : true;

	useEffect(() => {
		if (images.length > 0) {
			setSelectedIndex(0);
			hasFirstImageLoadedRef.current = false;
			consumedImageIdsRef.current.clear();
		}
	}, [playgroundResult.id]);

	// Unified handler that calls both callbacks for compatibility
	const handleSelectImage = useCallback(
		(index: number) => {
			setSelectedIndex(index);
			const image = images[index];
			if (image) {
				// Call both callbacks if provided for backwards compatibility
				onImageSelect?.(image, index);
				onSelectedImageChange?.(image, index);
			}
		},
		[images, onImageSelect, onSelectedImageChange],
	);

	const hasNotifiedRef = useRef<string | null>(null);
	useEffect(() => {
		const currentImage = images[selectedIndex];
		if (
			effectiveIsSelected &&
			currentImage?.url &&
			hasNotifiedRef.current !== `${playgroundResult.id}-${selectedIndex}-${currentImage.url}`
		) {
			hasNotifiedRef.current = `${playgroundResult.id}-${selectedIndex}-${currentImage.url}`;
			onImageSelect?.(currentImage, selectedIndex);
			onSelectedImageChange?.(currentImage, selectedIndex);
		}
	}, [effectiveIsSelected, selectedIndex, playgroundResult.id, images, onImageSelect, onSelectedImageChange]);

	const handleSuccessPulling = useCallback(
		(image: PlaygroundImage, index: number, result?: any) => {
			if (!hasFirstImageLoadedRef.current) {
				hasFirstImageLoadedRef.current = true;
				setSelectedIndex(index);
			}
			const imageId = image.id || `idx-${index}`;
			if (anonymousFeature && !authStore.isLoggedIn && !consumedImageIdsRef.current.has(imageId)) {
				consumedImageIdsRef.current.add(imageId);
				anonymousUsageStore.consumeToken(anonymousFeature);
			}
			onSuccessPulling?.(image, result);
		},
		[onSuccessPulling, anonymousFeature, authStore.isLoggedIn, anonymousUsageStore],
	);

	const fullScreenImages = images
		.filter((image) => !image.error && image.url)
		.map((image) => ({
			src: image.url,
			id: image.id,
			...getImageProps?.(image)?.fullScreenProps,
		}));

	useEffect(() => {
		if (selectedIndex >= images.length && images.length > 0) {
			setSelectedIndex(images.length - 1);
		}
	}, [images.length, selectedIndex]);

	useEffect(() => {
		setIsFullscreenViewerOpen(!!fullScreenProps?.open);
	}, [fullScreenProps?.open]);

	const handleOpenFullScreen = useCallback(
		(index: number) => {
			const fullScreenIndex = fullScreenImages.findIndex((img) => img.id === images[index]?.id);
			setIsFullscreenViewerOpen(true);
			setDisplayMode("arrows");
			if (fullScreenIndex >= 0) {
				setSelectedIndex(fullScreenIndex);
			}
		},
		[fullScreenImages, images],
	);

	const handleCloseFullScreen = useCallback(() => {
		setIsFullscreenViewerOpen(false);
		fullScreenProps?.onClose?.();
		setDisplayMode(fullScreenProps?.displayMode);
	}, [fullScreenProps]);

	const instantDownloadImage = useCallback(
		async (image: PlaygroundImage) => {
			const props = getImageProps?.(image);
			await exportImage(
				image.url,
				props?.downloadProps?.fileName || playgroundStore.getFileName(image),
				1,
				"download",
			);
		},
		[exportImage, playgroundStore, getImageProps],
	);

	if (!images.length) return null;

	return (
		<Box className={clsx(styles.labGalleryView, className)}>
			<Box className={styles.mainImageContainer}>
				{images.map((image, index) => (
					<LabGalleryImageItem
						key={image.id || index}
						image={image}
						isSelected={index === selectedIndex}
						scrollPosition={scrollPosition}
						renderImageOverlay={renderImageOverlay}
						renderImageExtra={renderImageExtra}
						extraProps={getImageProps?.(image) || {}}
						onOpenFullScreen={() => handleOpenFullScreen(index)}
						onSuccessPulling={(result) => handleSuccessPulling(image, index, result)}
						onErrorPulling={(errorType) => onErrorPulling?.(image, errorType)}
						abortController={abortController}
					/>
				))}
			</Box>
			{footerControls}
			<ThumbnailSelector images={images} selectedIndex={selectedIndex} onSelect={handleSelectImage} />
			{isFullscreenViewerOpen && (
				<FullScreenViewer
					{...fullScreenProps}
					open={isFullscreenViewerOpen}
					images={fullScreenImages.map((img) => ({ ...img, openCompare: true }))}
					currentIndex={selectedIndex}
					onClose={handleCloseFullScreen}
					displayMode={displayMode}
					onDownload={() => {
						const currentImage = images[selectedIndex];
						if (currentImage) instantDownloadImage(currentImage);
					}}
				/>
			)}
		</Box>
	);
};

export default trackWindowScroll(observer(LabGalleryView));
